import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { subscribeToUserProfile, getUserProfile } from '../firebase/firestore';
import {
  signUp as firebaseSignUp,
  signIn as firebaseSignIn,
  signInWithGoogle as firebaseSignInWithGoogle,
  signOut as firebaseSignOut,
  resetPassword as firebaseResetPassword,
  resendVerificationEmail,
  updateDisplayName,
  updatePhotoURL,
  changeEmail,
  changePassword,
  initRecaptchaVerifier,
  sendPhoneOTP,
  verifyPhoneOTP,
  cleanupRecaptcha,
  reloadUser,
  hasPhoneLinked,
  getLinkedPhone,
  syncPhoneToFirestore,
  unlinkPhone,
} from '../firebase/auth';

/**
 * Auth Context
 *
 * Provides authentication state and methods throughout the app.
 * Combines Firebase Auth user with Firestore user profile.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Firebase Auth user
  const [user, setUser] = useState(null);
  // Firestore user profile
  const [userProfile, setUserProfile] = useState(null);
  // Loading state
  const [loading, setLoading] = useState(true);
  // Auth operation loading
  const [authLoading, setAuthLoading] = useState(false);
  // Error state
  const [error, setError] = useState(null);

  // Subscribe to Firebase Auth state changes
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (!firebaseUser) {
        setUserProfile(null);
        setLoading(false);
        return;
      }

      // Fetch initial profile while setting up subscription
      try {
        const profile = await getUserProfile(firebaseUser.uid);
        setUserProfile(profile);
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }

      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  // Subscribe to user profile changes (real-time)
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribeProfile = subscribeToUserProfile(user.uid, (profile) => {
      setUserProfile(profile);
    });

    return () => unsubscribeProfile();
  }, [user?.uid]);

  // Check for phone state inconsistency (Auth has phone but Firestore doesn't)
  useEffect(() => {
    if (!user || !userProfile) return;

    const checkPhoneSync = async () => {
      const authHasPhone = hasPhoneLinked();
      const firestoreHasPhone = userProfile.phoneVerified === true;

      if (authHasPhone && !firestoreHasPhone) {
        console.log('[AuthContext] Phone linked in Auth but not in Firestore, syncing...');
        try {
          await syncPhoneToFirestore();
          console.log('[AuthContext] Phone synced to Firestore');
        } catch (err) {
          console.error('[AuthContext] Failed to sync phone to Firestore:', err);
        }
      }
    };

    checkPhoneSync();
  }, [user, userProfile?.phoneVerified]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ============================================
  // AUTH METHODS
  // ============================================

  const signUp = useCallback(async (email, password, displayName) => {
    setAuthLoading(true);
    setError(null);
    try {
      const user = await firebaseSignUp(email, password, displayName);
      return user;
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email, password) => {
    setAuthLoading(true);
    setError(null);
    try {
      const user = await firebaseSignIn(email, password);
      return user;
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setAuthLoading(true);
    setError(null);
    try {
      const user = await firebaseSignInWithGoogle();
      return user;
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setAuthLoading(true);
    setError(null);
    try {
      await firebaseSignOut();
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    setAuthLoading(true);
    setError(null);
    try {
      await firebaseResetPassword(email);
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const resendEmailVerification = useCallback(async () => {
    setAuthLoading(true);
    setError(null);
    try {
      await resendVerificationEmail();
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      await reloadUser();
      // Force re-render with updated user
      setUser({ ...auth.currentUser });
    } catch (err) {
      console.error('Error refreshing user:', err);
    }
  }, []);

  // ============================================
  // PROFILE UPDATE METHODS
  // ============================================

  const updateName = useCallback(async (displayName) => {
    setAuthLoading(true);
    setError(null);
    try {
      await updateDisplayName(displayName);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const updatePhoto = useCallback(async (photoURL) => {
    setAuthLoading(true);
    setError(null);
    try {
      await updatePhotoURL(photoURL);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const updateUserEmail = useCallback(async (newEmail, currentPassword) => {
    setAuthLoading(true);
    setError(null);
    try {
      await changeEmail(newEmail, currentPassword);
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const updateUserPassword = useCallback(async (currentPassword, newPassword) => {
    setAuthLoading(true);
    setError(null);
    try {
      await changePassword(currentPassword, newPassword);
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // ============================================
  // PHONE VERIFICATION METHODS
  // ============================================

  const setupRecaptcha = useCallback((containerId, callback) => {
    return initRecaptchaVerifier(containerId, callback);
  }, []);

  const sendOTP = useCallback(async (phoneNumber) => {
    setAuthLoading(true);
    setError(null);
    try {
      const result = await sendPhoneOTP(phoneNumber);
      return result;
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const verifyOTP = useCallback(async (code) => {
    setAuthLoading(true);
    setError(null);
    try {
      const phoneNumber = await verifyPhoneOTP(code);
      return phoneNumber;
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const cleanupPhoneAuth = useCallback(() => {
    cleanupRecaptcha();
  }, []);

  const resetPhone = useCallback(async () => {
    setAuthLoading(true);
    setError(null);
    try {
      await unlinkPhone();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Check phone state for debugging
  const getPhoneState = useCallback(() => {
    return {
      authHasPhone: hasPhoneLinked(),
      authPhoneNumber: getLinkedPhone(),
      firestorePhone: userProfile?.phone || null,
      firestoreVerified: userProfile?.phoneVerified || false,
    };
  }, [userProfile]);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const isAuthenticated = !!user;
  // Skip email verification in emulator mode
  const isEmailVerified = import.meta.env.VITE_USE_EMULATORS === 'true'
    ? true
    : (user?.emailVerified ?? false);
  // Skip phone verification in emulator mode
  const isPhoneVerified = import.meta.env.VITE_USE_EMULATORS === 'true'
    ? true
    : (userProfile?.phoneVerified ?? false);
  const isFullyVerified = isEmailVerified && isPhoneVerified;

  // Check if user can perform actions requiring verification
  const canCreateRaffle = isAuthenticated && isPhoneVerified;
  const canEnterRaffle = isAuthenticated && isPhoneVerified;

  // Check if user has Stripe Connect set up
  const hasStripeConnect = userProfile?.stripeConnectStatus === 'active';
  const stripeConnectPending = userProfile?.stripeConnectStatus === 'pending';

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = {
    // State
    user,
    userProfile,
    loading,
    authLoading,
    error,

    // Computed
    isAuthenticated,
    isEmailVerified,
    isPhoneVerified,
    isFullyVerified,
    canCreateRaffle,
    canEnterRaffle,
    hasStripeConnect,
    stripeConnectPending,

    // Auth methods
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    resendEmailVerification,
    refreshUser,

    // Profile methods
    updateName,
    updatePhoto,
    updateUserEmail,
    updateUserPassword,

    // Phone verification
    setupRecaptcha,
    sendOTP,
    verifyOTP,
    cleanupPhoneAuth,
    resetPhone,
    getPhoneState,

    // Clear error manually
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Convert Firebase auth error codes to user-friendly messages
 */
function getAuthErrorMessage(code) {
  const messages = {
    'auth/email-already-in-use': 'This email is already registered.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/operation-not-allowed': 'Operation not allowed. Phone authentication may not be enabled.',
    'auth/weak-password': 'Password is too weak. Use at least 8 characters.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Check your connection.',
    'auth/popup-closed-by-user': 'Sign-in cancelled.',
    'auth/requires-recent-login': 'Please sign in again to perform this action.',
    'auth/invalid-verification-code': 'Invalid verification code. Please check and try again.',
    'auth/invalid-phone-number': 'Invalid phone number. Use format: +353 87 123 4567',
    'auth/missing-phone-number': 'Please enter a phone number.',
    'auth/quota-exceeded': 'SMS quota exceeded. Please try again later.',
    'auth/credential-already-in-use': 'This phone number is already linked to another account. Please use a different number.',
    'auth/provider-already-linked': 'A phone number is already linked to your account. Reset to use a different number.',
    'auth/captcha-check-failed': 'reCAPTCHA verification failed. Please refresh the page and try again.',
    'auth/web-storage-unsupported': 'Your browser does not support web storage. Please enable cookies.',
    'auth/code-expired': 'Verification code has expired. Please request a new code.',
    'auth/session-expired': 'Session expired. Please request a new verification code.',
  };

  return messages[code] || `An error occurred (${code || 'unknown'}). Please try again.`;
}
